import React from 'react';
import { Flex } from '../box';
import Text from '../text';
import { RoundButton } from '../button';
import Icon from '../icon';
import DotsLoader from '../dots-loader';

interface ArrowPagerProps {
  currentPage?: number;
  pageSize?: number;
  totalCount?: number;
  showTotal?: boolean;
  onChange?: ({ page }: { page: number }) => void;
  isLoading?: boolean;
  amountOfPages?: number;
  disabled?: boolean;
}

const ArrowsPager = ({
  currentPage = 1,
  pageSize = 2,
  totalCount = 0,
  showTotal,
  onChange,
  isLoading,
  amountOfPages = 1,
  disabled = false,
}: ArrowPagerProps): React.JSX.Element | null => {
  const onPrevOrNext = (page: number) => {
    onChange?.({ page });
  };
  const selectedItemIndex = (currentPage - 1) * pageSize + 1;

  const topItem = Math.min(selectedItemIndex + pageSize - 1, totalCount) || 0;
  const fromItem = Math.min(selectedItemIndex, pageSize * currentPage) || 0;
  const isEmpty = topItem === 0 && fromItem === 0;

  if (isEmpty) {
    return null;
  }
  return (
    <Flex data-testid={'arrows-pager'} justifyContent={'center'} alignItems={'center'}>
      {amountOfPages !== 1 && (
        <RoundButton
          disabled={currentPage === 1 || disabled}
          onClick={() => onPrevOrNext(currentPage - 1)}
          variant={'secondary-clean'}
          size={'sm'}
          aria-label={'previous page'}
          data-testid={'arrows-pager-prev'}
        >
          <Icon name={'ANGLE_LEFT'} size={'xl'} />
        </RoundButton>
      )}
      <Flex data-testid={'arrows-pagination-text'} marginHorizontal={'xs'} justifyContent={'center'}>
        {isLoading ? (
          <DotsLoader size={'xs'} />
        ) : (
          <Text>
            <span> {`${fromItem} - ${topItem}`}</span>
            {showTotal && !!totalCount && <span>{` / ${totalCount?.toLocaleString?.()}`}</span>}
          </Text>
        )}
      </Flex>
      {amountOfPages !== 1 && (
        <RoundButton
          onClick={() => onPrevOrNext(currentPage + 1)}
          variant={'secondary-clean'}
          size={'sm'}
          disabled={currentPage === amountOfPages || disabled}
          aria-label={'next page'}
          data-testid={'arrows-pager-next'}
        >
          <Icon name={'ANGLE_RIGHT'} size={'xl'} />
        </RoundButton>
      )}
    </Flex>
  );
};

export default ArrowsPager;
